import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { runInAction } from 'mobx';
import type { InputBaseModel, NodeBaseModel } from '../models';
import type { SourceModelBase } from '../../custom_shared/models';
import type { ContractChangeReferenceDto } from '../history/types';
import type { SubmissionScope, ContractsIngestionRuntimeInputDto } from '../types';
import ContractDraftHistory from '../history/ContractDraftHistory';
import {
    contractInputGroups,
    contractInputGroupEntries,
    contractReferenceKey as key,
    contractRecordReference,
    draftScopeKey
} from '../history/ContractDraftScope';
import { InputFactory } from '../factories';
import { InstanceUtils } from '../misc';
import ContractSourceRequests from './ContractSourceRequests';

interface InputDraft {
    input: InputBaseModel;
    dto: ContractsIngestionRuntimeInputDto;
    value: unknown;
    target: string;
    scope: string;
}

type ControlDraft =
    | { target: ContractChangeReferenceDto; kind: 'submissionScope'; value: SubmissionScope }
    | { target: ContractChangeReferenceDto; kind: 'skipSubmission'; value: boolean };

// The form owns unsaved values, repeated records and controls. History supplies
// only the pending references, so acknowledgment cannot discard a newer draft.
export default class ContractDraftOverlay {
    private controls = new Map<string, ControlDraft>();
    private values = new Map<string, InputDraft>();
    private records = new Map<string, InputDraft[]>();

    constructor(
        private readonly nodes: () => NodeBaseModel[],
        private readonly history: ContractDraftHistory,
        private readonly requests: ContractSourceRequests,
        private readonly sources: () => SourceModelBase[]
    ) {}

    setSubmissionScope(target: ContractChangeReferenceDto, value: SubmissionScope) {
        this.retainControl({ target, kind: 'submissionScope', value });
    }

    setSkipSubmission(target: ContractChangeReferenceDto, value: boolean) {
        this.retainControl({ target, kind: 'skipSubmission', value });
    }

    clear() {
        this.controls.clear();
        this.values.clear();
        this.records.clear();
        this.history.setDeferredTargets([]);
    }

    reconcile(saveId: string | null | undefined, rebuild: () => void) {
        this.history.acknowledge(saveId);
        this.controls.forEach((draft, target) => {
            if (!this.history.hasTarget(draft.target)) {
                this.controls.delete(target);
            }
        });
        const groups = contractInputGroups(this.nodes());
        const pending = new Set(this.history.pendingTargets.map(key));
        this.values.forEach((_, target) => {
            if (!pending.has(target)) {
                this.values.delete(target);
            }
        });
        const recordScopes = new Set(
            this.history.pendingTargets
                .filter(target => target.recordId && key(contractRecordReference(target) ?? target) === key(target))
                .map(target => draftScopeKey(target.nodeId!, target.layerId, target.sectionId))
        );
        this.records.forEach((_, scope) => {
            if (!recordScopes.has(scope)) {
                this.records.delete(scope);
            }
        });
        groups.forEach(group => {
            const entries = contractInputGroupEntries(group);
            if (recordScopes.has(group.id)) {
                this.records.set(
                    group.id,
                    entries
                        .filter(entry => entry.reference.recordId)
                        .map(entry => this.inputDraft(entry.input, entry.key, group.id))
                );
            }
            entries.forEach(({ input, key: target }) => {
                if (pending.has(target)) {
                    this.values.set(target, this.inputDraft(input, target, group.id));
                }
            });
        });
        const valuesByScope = new Map<string, InputDraft[]>();
        this.values.forEach(draft => {
            const drafts = valuesByScope.get(draft.scope) ?? [];
            drafts.push(draft);
            valuesByScope.set(draft.scope, drafts);
        });
        this.requests.replaceGraph(() =>
            this.history.withoutCapture(() => {
                rebuild();
                contractInputGroups(this.nodes()).forEach(group => {
                    const materialize = (draft: InputDraft) =>
                        draft.input.ownerNode === group.node
                            ? draft.input
                            : InputFactory.createInput(group.node, draft.dto, this.sources());
                    const repeated = this.records.get(group.id);
                    let inputs = repeated
                        ? [
                              ...contractInputGroupEntries(group)
                                  .filter(entry => !entry.reference.recordId)
                                  .map(entry => entry.input),
                              ...repeated.map(materialize)
                          ]
                        : group.inputs;
                    let entries = contractInputGroupEntries(group, inputs);
                    const availableTargets = new Set(entries.map(entry => entry.key));
                    // An earlier response can remove a field edited after submission.
                    const missing = (valuesByScope.get(group.id) ?? []).filter(
                        draft => !availableTargets.has(draft.target)
                    );
                    if (repeated || missing.length) {
                        inputs = [...inputs, ...missing.map(materialize)];
                        group.replace(inputs);
                        entries = contractInputGroupEntries(group, inputs);
                    }
                    const repeatedByGuid = new Map<string, InputDraft>();
                    repeated?.forEach(draft => {
                        if (!repeatedByGuid.has(draft.dto.guid)) {
                            repeatedByGuid.set(draft.dto.guid, draft);
                        }
                    });
                    entries.forEach(({ input, key: target }) => {
                        const draft = this.values.get(target) ?? repeatedByGuid.get(input.guid);
                        if (draft && !isEqual(input.value, draft.value)) {
                            input.setValue(draft.value);
                        }
                    });
                });
                const availableControls = new Set<string>();
                this.controls.forEach((draft, target) => {
                    if (this.applyControl(draft)) {
                        availableControls.add(target);
                    }
                });
                const availableScopes = new Set(contractInputGroups(this.nodes()).map(group => group.id));
                // Keep drafts for removed scopes outside the form until that scope
                // returns. An unrelated save cannot submit or acknowledge them.
                this.history.setDeferredTargets(
                    this.history.pendingTargets.filter(target => {
                        if (this.controls.has(key(target))) {
                            return !availableControls.has(key(target));
                        }
                        return (
                            target.nodeId &&
                            !availableScopes.has(draftScopeKey(target.nodeId, target.layerId, target.sectionId))
                        );
                    })
                );
            })
        );
    }

    private inputDraft(input: InputBaseModel, target: string, scope: string): InputDraft {
        return {
            input,
            dto: cloneDeep(input.dto),
            value: cloneDeep(input.value),
            target,
            scope
        };
    }

    private retainControl(draft: ControlDraft) {
        this.history.capture(draft.target);
        this.controls.set(key(draft.target), draft);
        runInAction(() => this.applyControl(draft));
    }

    private applyControl(draft: ControlDraft) {
        let applied = false;
        const visit = (nodes: NodeBaseModel[]): void =>
            nodes.forEach(node => {
                if (node.id === draft.target.nodeId) {
                    if (draft.kind === 'submissionScope') {
                        if (InstanceUtils.isSectionsNode(node) && !draft.target.layerId) {
                            node.submissionScope = draft.value;
                            applied = true;
                        }
                        if (InstanceUtils.isLayersWithSectionsNode(node)) {
                            const layer = node.layers.find(candidate => candidate.id === draft.target.layerId);
                            if (layer) {
                                layer.submissionScope = draft.value;
                                applied = true;
                            }
                        }
                    } else if (InstanceUtils.isLayersNode(node)) {
                        const configuration = node.layers.find(
                            layer => layer.id === draft.target.layerId
                        )?.layerConfiguration;
                        if (configuration) {
                            configuration.skipSubmission = draft.value;
                            applied = true;
                        }
                    }
                }
                visit(node.childNodes);
            });
        visit(this.nodes());
        return applied;
    }
}
